import {
  Component,
  ViewEncapsulation,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core'
import { MatInputModule } from '@angular/material/input'
import { MatButtonModule } from '@angular/material/button'
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms'
import { MatFormFieldModule } from '@angular/material/form-field'
import { MatIconModule } from '@angular/material/icon'
import { ActivatedRoute, Router, RouterModule } from '@angular/router'
import { USER_ROLES } from '../../../constants/auth'
import { Dialog } from '@angular/cdk/dialog'
import { firstValueFrom } from 'rxjs'
import { AuthService } from '../../../services/auth.service'
import { DialogService } from '../../../services/dialog.service'
import { ErrorService } from '../../../services/error.service'
import { SpinnerService } from '../../../services/spinner.service'
import { ApiError } from '../../../types/apiError'
import {
  LoginBody,
  LoginResponse,
  MfaResponse,
  SessionResponse,
} from '../../../types/auth'
import { ROUTES } from '../../../constants/routes'
import { TranslationService } from '../../../services/translation.service'
import { MfaComponent } from './mfa/mfa.component'

@Component({
  selector: 'app-login',
  imports: [
    MatFormFieldModule,
    FormsModule,
    ReactiveFormsModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    RouterModule,
    MfaComponent,
  ],
  templateUrl: './login.component.html',
  styleUrl: './login.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  encapsulation: ViewEncapsulation.None,
})
export class LoginComponent {
  dialog = inject(Dialog)
  private authService = inject(AuthService)
  private errorService = inject(ErrorService)
  private spinnerService = inject(SpinnerService)
  private dialogService = inject(DialogService)
  private router = inject(Router)
  private route = inject(ActivatedRoute)
  translationService = inject(TranslationService)

  loginForm = new FormGroup({
    email: new FormControl<string>('', [Validators.email, Validators.required]),
    password: new FormControl<string>('', [Validators.required]),
  })

  isMfaRequired = false
  mfaUserId?: string

  /**
   * Sends request to login user
   */
  async login() {
    try {
      if (!this.loginForm.valid) {
        return
      }

      this.spinnerService.startSpinner()

      const loginBody: LoginBody = this.loginForm.value as LoginBody

      // Await the login response
      const loginResponse = await firstValueFrom(
        this.authService.login(loginBody),
      )

      // Check if MFA is required
      if (this.isMfaRequiredInResponse(loginResponse)) {
        this.spinnerService.stopSpinner()

        this.isMfaRequired = true
        this.mfaUserId = loginResponse.userId
        return
      }

      // Handle successful login case
      const sessionResponse = loginResponse as SessionResponse
      this.handleLoginSuccess(sessionResponse)
      this.spinnerService.stopSpinner()
    } catch (err) {
      this.spinnerService.stopSpinner()
      const apiErr = err as ApiError
      this.errorService.handleErr(this.dialogService, apiErr, this.dialog)
    }
  }

  /**
   * Handles login response success. Stores session in LS and navigates based on role
   * @param sessionResponse
   */
  handleLoginSuccess(sessionResponse: SessionResponse) {
    // Store the session
    this.authService.storeSessionInLS(sessionResponse)
    this.authService.currentUserRole.set(sessionResponse.user.role)

    // Only requisition and purchase order destinations are accepted.
    // MFA also uses this method, preserving the email destination after verification.
    const returnUrl = this.route.snapshot.queryParamMap.get('returnUrl')
    if (
      returnUrl &&
      /^\/(?:requisitions|purchase-orders)(?:\/[a-f0-9-]{36})?$/.test(
        returnUrl,
      ) &&
      [USER_ROLES.ADMIN, USER_ROLES.COMPLIANCE_OFFICER].includes(
        sessionResponse.user.role,
      )
    ) {
      this.router.navigateByUrl(returnUrl)
      return
    }

    // Navigate based on user role
    switch (sessionResponse.user.role) {
      case USER_ROLES.MASTER:
        this.router.navigate([ROUTES.ADMIN.path])
        break
      case USER_ROLES.ADMIN:
        this.router.navigate([ROUTES.ADMIN.path])
        break
      case USER_ROLES.COMPLIANCE_OFFICER:
        this.router.navigate([
          `${ROUTES.ADMIN.path}/${ROUTES.COMPANY.path}/${ROUTES.COMPANY_PRODUCTS.path}`,
        ])
        break
      default:
        this.router.navigate([ROUTES.HOME.path])
    }
  }

  isMfaRequiredInResponse(response: LoginResponse): response is MfaResponse {
    if ((response as MfaResponse).mfaRequired) {
      return true
    }

    return false
  }
}
